import { PRODUCT_NAME } from '@wednesday/contracts';
import type { ComponentProps, CSSProperties } from 'react';
import { Toaster as SonnerToaster } from 'sonner';

import { cn } from '@/shared/lib/cn';
import { useTheme } from '@/shared/lib/theme';

export function Card({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div className={cn('rounded-xl border border-border bg-surface p-6', className)} {...props} />
  );
}

/** The orange spark of the logo (the dot of its "i"); the same shape is the favicon. */
const LOGO_SPARK =
  'M413.01,91.4l3.42-.17-1.85,2.88c-2.82,4.38-2.82,10,0,14.38l1.85,2.88-3.42-.17c-5.2-.25-10.07,2.56-12.45,7.19l-1.57,3.04-1.57-3.04c-2.38-4.63-7.25-7.44-12.45-7.19l-3.42.17,1.85-2.88c2.82-4.38,2.82-10,0-14.38l-1.85-2.88,3.42.17c5.2.25,10.07-2.56,12.45-7.19l1.57-3.04,1.57,3.04c2.38,4.63,7.25,7.44,12.45,7.19Z';

/** The letters of the "tasklist" wordmark, left to right, drawn in the text color so they follow the theme. */
const LOGO_LETTERS = [
  'M115.4,160.75c-23.1,0-31.47-11.64-31.47-29.36v-29.89l35.09-9.35v11.37l11.02-2.03v29.1l-37.12-5.82c-.71-.09-1.15.26-1.15.97,0,.44.18.79.79.88l37.47,5.82v26.54c-3.79,1.06-9.08,1.76-14.64,1.76Z',
  'M151.72,160.75c-11.11,0-20.37-5.82-20.37-18.07s8.02-17.9,20.01-17.9c4.06,0,9.52.71,14.02,2.29-2.73-3-7.67-4.06-14.02-4.06-10.23,0-15.87,3.97-17.9,7.32h-.62c0-20.1,10.67-30.59,34.83-30.59s35.18,11.64,35.18,29.36v29.89h-35.09c6.96-3.08,9.43-9.08,9.43-12.78,0-.53-.35-.79-.79-.79-.71,0-.79.35-.88,1.06-1.5,11.11-15.34,14.28-23.81,14.28ZM166.53,148.4c.71,0,1.23-.62,1.23-3.35v-11.11c0-1.76-.26-3.26-.88-4.58-1.32,1.59-1.76,4.58-1.76,11.02s.44,8.02,1.41,8.02Z',
  'M236.53,99.74c22.48,0,32.27,8.99,30.07,29.18h-.71c-4.94-6.96-13.93-10.76-31.56-10.76-.88,0-1.41.18-1.41.88s.53.97,1.41.97c16.31,0,33.42,3.97,33.42,20.54,0,12.26-10.49,20.19-32.89,20.19s-33.15-9.35-30.68-31.65h.62c3.79,9.52,15.61,13.23,33.42,13.23.88,0,1.32-.26,1.32-.97,0-.62-.44-.88-1.32-.88-16.84,0-33.42-3.35-33.42-19.48,0-12.08,9.35-21.25,31.74-21.25Z',
  'M335.19,129.8l7.94,29.18h-36.15l-1.23-27.24h-1.59v27.24h-35.09v-67.89h35.09v14.02c-6.61,6.26-9.88,15.52-9.88,24.69,0,.53.26.97.97.97s.97-.44.97-.97c0-17.63,10.93-30.06,28.48-30.06,6.97,0,13.05.88,16.22,1.94l-5.73,26.27h-29.89c-.7,0-1.14.18-1.14.88,0,.79.44.97,1.14.97h29.89Z',
  'M344.27,91.09h35.09v67.89h-35.09v-67.89Z',
  'M401.33,121.24l-2.56,4.97-2.56-4.97c-2.39-4.65-7.31-7.49-12.55-7.24l-2.29.11v44.88h35.09v-44.86l-2.59-.12c-5.25-.25-10.15,2.59-12.55,7.24Z',
  'M450.42,99.74c22.48,0,32.27,8.99,30.07,29.18h-.71c-4.94-6.96-13.93-10.76-31.56-10.76-.88,0-1.41.18-1.41.88s.53.97,1.41.97c16.31,0,33.42,3.97,33.42,20.54,0,12.26-10.49,20.19-32.89,20.19s-33.15-9.35-30.68-31.65h.62c3.79,9.52,15.61,13.23,33.42,13.23.88,0,1.32-.26,1.32-.97,0-.62-.44-.88-1.32-.88-16.84,0-33.42-3.35-33.42-19.48,0-12.08,9.35-21.25,31.74-21.25Z',
  'M514.07,160.75c-23.1,0-31.47-11.64-31.47-29.36v-29.89l35.09-9.35v11.37l11.02-2.03v29.1l-37.12-5.82c-.71-.09-1.15.26-1.15.97,0,.44.18.79.79.88l37.47,5.82v26.54c-3.79,1.06-9.08,1.76-14.64,1.76Z',
] as const;

/** The square around the spark alone. */
const SPARK_VIEW_BOX = '377 79.3 44 44';

/**
 * The Tasklist logo (D39): the wordmark, or only its spark when `withName` is false. Motion (D41):
 * `enter` brings the letters in one by one and bursts the spark in after them; `hover` turns the spark
 * when the pointer is over the logo.
 */
export function Logo({
  className,
  withName = true,
  motion = [],
}: {
  className?: string;
  withName?: boolean;
  motion?: readonly ('enter' | 'hover')[];
}) {
  return (
    <svg
      role="img"
      aria-label={PRODUCT_NAME}
      viewBox={withName ? '83 80 447 82' : SPARK_VIEW_BOX}
      className={cn(
        'text-foreground',
        withName ? 'h-7 w-auto' : 'size-7',
        motion.includes('enter') && 'logo-enter',
        motion.includes('hover') && 'logo-hover',
        className,
      )}
    >
      {withName &&
        LOGO_LETTERS.map((d, index) => (
          <path
            key={d}
            d={d}
            fill="currentColor"
            className="logo-letter"
            style={{ '--i': index } as CSSProperties}
          />
        ))}
      <path d={LOGO_SPARK} fill="#ff6d00" className="logo-spark" />
    </svg>
  );
}

/**
 * The loading indicator (D41): the logo's spark turning, or slowly fading when motion is reduced.
 * `decorative` hides it from assistive technology when text next to it already says what is happening.
 */
export function Spinner({
  className,
  label = 'Loading',
  decorative = false,
}: {
  className?: string;
  label?: string;
  decorative?: boolean;
}) {
  return (
    <svg
      {...(decorative ? { 'aria-hidden': true } : { role: 'status', 'aria-label': label })}
      viewBox={SPARK_VIEW_BOX}
      className={cn('spinner-spark size-5 text-brand', className)}
    >
      <path d={LOGO_SPARK} fill="currentColor" />
    </svg>
  );
}

/** App-wide toasts (errors with reference IDs; undo actions later). */
export function Toaster() {
  const { resolved } = useTheme();
  return (
    <SonnerToaster
      theme={resolved}
      position="bottom-left"
      closeButton
      toastOptions={{ classNames: { toast: 'font-sans text-sm' } }}
    />
  );
}
